/**
 * Client-side per-pixel min-altitude composite.
 *
 * For every per-observer COG, we cache its decoded Float32 raster + the bounds
 * of its extent inside the union grid. When the user toggles checkboxes, we
 * walk the enabled rasters and produce a new Float32 result via pixel-wise min.
 *
 * Only works correctly when all per-observer COGs share the union's grid
 * (which they do — they're all reprojected to EPSG:3857 with the same target
 * transform from the same job).
 */

import { fromUrl, type GeoTIFFImage } from "geotiff";
import { unzipSync, strFromU8 } from "fflate";

export interface ObserverRaster {
  stationId: number;
  data: Float32Array;
  width: number;
  height: number;
  nodata: number;
  // bbox in EPSG:3857 meters, used for the union layer's quad geometry
  bounds: { left: number; bottom: number; right: number; top: number };
}

/** Fetch + decode a per-observer COG. */
export async function loadObserverRaster(url: string, stationId: number): Promise<ObserverRaster> {
  const tiff = await fromUrl(url);
  const image: GeoTIFFImage = await tiff.getImage();
  const width = image.getWidth();
  const height = image.getHeight();
  const nodata = Number(image.getGDALNoData() ?? -32767);
  const data = new Float32Array((await image.readRasters({ interleave: true })) as Float32Array);
  const [left, bottom, right, top] = image.getBoundingBox();
  return { stationId, data, width, height, nodata, bounds: { left, bottom, right, top } };
}

/** Unzip a coverage bundle in-memory and return the parsed manifest.json.
 * Returns null if not a valid bundle. Used to populate per-station UI from a
 * fresh server-side job without a separate API endpoint. */
export async function extractManifestJsonFromBundle(buf: ArrayBuffer): Promise<Record<string, unknown> | null> {
  try {
    const entries = unzipSync(new Uint8Array(buf), { filter: (f) => f.name === "manifest.json" });
    const m = entries["manifest.json"];
    if (!m) return null;
    return JSON.parse(strFromU8(m));
  } catch {
    return null;
  }
}

export interface CompositeResult {
  data: Float32Array;
  width: number;
  height: number;
  bounds: { left: number; bottom: number; right: number; top: number };
}

/**
 * Build a composite Float32 raster where each output pixel = min over the
 * enabled rasters' valid values at that world location.
 *
 * Per-observer COGs have DIFFERENT bounds and dimensions (each is reprojected
 * independently from its own observer-clipped DEM), so we can't index by linear
 * position. Instead the output spans the union of all enabled rasters' bounds
 * at the highest input resolution; for each output pixel we sample each raster
 * via inverse pixel transform (nearest-neighbor — appropriate for min-altitude
 * step values).
 *
 * Cost: O(width × height × N_rasters). For typical MCV jobs (~3000² output,
 * 12 rasters) this is ~100M iterations / a few hundred ms in JS — acceptable
 * for an interactive checkbox toggle.
 */
export function compositeMin(
  rasters: ObserverRaster[],
  nodata: number,
  target?: { width: number; height: number; bounds: { left: number; bottom: number; right: number; top: number } },
): CompositeResult {
  if (rasters.length === 0) {
    return { data: new Float32Array(0), width: 0, height: 0,
             bounds: { left: 0, bottom: 0, right: 0, top: 0 } };
  }

  let left: number, right: number, bottom: number, top: number, width: number, height: number;
  if (target) {
    // Explicit target grid (typically the union COG's bounds = the user's bbox).
    // Per-observer rasters spill outside the bbox; clipping to the target keeps
    // the composite shape consistent with the original union view.
    left = target.bounds.left;
    right = target.bounds.right;
    bottom = target.bounds.bottom;
    top = target.bounds.top;
    width = target.width;
    height = target.height;
  } else {
    // Fall back to union of input bounds at the highest input resolution
    left = Infinity; right = -Infinity; bottom = Infinity; top = -Infinity;
    for (const r of rasters) {
      if (r.bounds.left < left) left = r.bounds.left;
      if (r.bounds.right > right) right = r.bounds.right;
      if (r.bounds.bottom < bottom) bottom = r.bounds.bottom;
      if (r.bounds.top > top) top = r.bounds.top;
    }
    let bestResX = Infinity, bestResY = Infinity;
    for (const r of rasters) {
      const rx = (r.bounds.right - r.bounds.left) / r.width;
      const ry = (r.bounds.top - r.bounds.bottom) / r.height;
      if (rx < bestResX) bestResX = rx;
      if (ry < bestResY) bestResY = ry;
    }
    width = Math.max(1, Math.round((right - left) / bestResX));
    height = Math.max(1, Math.round((top - bottom) / bestResY));
  }
  const resX = (right - left) / width;
  const resY = (top - bottom) / height;
  const out = new Float32Array(width * height);
  out.fill(nodata);

  // Precompute per-raster transform constants
  const xforms = rasters.map((r) => ({
    data: r.data,
    width: r.width,
    height: r.height,
    nodata: r.nodata,
    left: r.bounds.left,
    top: r.bounds.top,
    invResX: r.width / (r.bounds.right - r.bounds.left),
    invResY: r.height / (r.bounds.top - r.bounds.bottom),
    bRight: r.bounds.right,
    bBottom: r.bounds.bottom,
  }));

  for (let oy = 0; oy < height; oy++) {
    const wy = top - (oy + 0.5) * resY;
    const rowOffset = oy * width;
    for (let ox = 0; ox < width; ox++) {
      const wx = left + (ox + 0.5) * resX;
      let minv = nodata;
      for (const x of xforms) {
        if (wx < x.left || wx >= x.bRight) continue;
        if (wy <= x.bBottom || wy > x.top) continue;
        const sx = (wx - x.left) * x.invResX | 0;       // floor via |0 (positive)
        const sy = (x.top - wy) * x.invResY | 0;
        if (sx < 0 || sx >= x.width || sy < 0 || sy >= x.height) continue;
        const v = x.data[sy * x.width + sx];
        if (v === x.nodata || !(v > -1e30) || !Number.isFinite(v)) continue;
        if (minv === nodata || v < minv) minv = v;
      }
      out[rowOffset + ox] = minv;
    }
  }

  return { data: out, width, height, bounds: { left, bottom, right, top } };
}
